import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import { callCenterApi } from '@/api/callcenter'
import { apiClient } from '@/api/client'
import { useAuthStore } from '@/store/auth'
import CustomerSelectModal, { SelectCustomerItem } from '@/components/shared/CustomerSelectModal'
import BankSelectModal from '@/components/shared/BankSelectModal'
import type { Bank } from '@/api/banks'
import { bankApi } from '@/api/banks'

interface ConversationsProps {
  teamIdOverride?: number
  requireTeamForAdmin?: boolean
}

const inputCls = "border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
const smallSelectCls = "text-xs px-2 py-1 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
const ghostBtnCls = "px-3 py-2 border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
const smallGhostBtnCls = "px-2 py-1 border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"

const CallCenterConversations: React.FC<ConversationsProps> = ({ teamIdOverride, requireTeamForAdmin }) => {
  const { user } = useAuthStore()
  const role = user?.role
  const teamId = (user as any)?.team_id as number | undefined
  const effectiveTeamId = teamIdOverride ?? teamId

  const [selectedCustomer, setSelectedCustomer] = useState<SelectCustomerItem | null>(null)
  const [customerProfile, setCustomerProfile] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'info' | 'notes' | 'docs' | 'investments' | 'transactions'>('info')
  const [docs, setDocs] = useState<any[]>([])
  const [uploading, setUploading] = useState(false)
  const [investments, setInvestments] = useState<any[]>([])
  const [invForm, setInvForm] = useState<{ amount?: number; duration?: number; rate?: number; bank_id?: number; notes?: string; status?: string }>({})
  const [bankModal, setBankModal] = useState(false)
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null)
  const [calc, setCalc] = useState<{ maturity?: string; expected?: number } >({})
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<any>(null)
  const isManager = role === 'callcenter_admin' || role === 'admin'
  const [txForm, setTxForm] = useState<{ type?: string; amount?: number; status?: string; reference_number?: string; description?: string }>({})
  const [txList, setTxList] = useState<any[]>([])

  const t = (tr: string, de: string) => (role === 'staff' ? de : tr)

  const getTxTypeLabel = (tkey: string) => ({
    deposit: t('Para Yatırma','Einzahlung'),
    bonus: t('Yeni Müşteri Bonusu','Neukundenbonus'),
    withdrawal: t('Para Çekme','Auszahlung'),
    interest_payment: t('Faiz Ödemesi','Zinszahlung'),
    investment_creation: t('Yatırım Oluşturma','Anlageerstellung'),
    investment_maturity: t('Yatırım Vadesi','Anlagefälligkeit'),
    penalty: t('Ceza','Strafe'),
    fee: t('Ücret','Gebühr'),
    refund: t('İade','Rückerstattung'),
    investment: t('Yatırım','Anlage'),
  } as Record<string, string>)[tkey] || tkey

  const getTxTypeColor = (t: string) => ({
    deposit: 'bg-primary-50 text-primary-700 border-primary-200',         // Mavi - Para Yatırma
    bonus: 'bg-amber-50 text-amber-700 border-amber-200',        // Amber - Yeni Müşteri Bonusu
    withdrawal: 'bg-orange-50 text-orange-700 border-orange-200', // Turuncu - Para Çekme
    interest_payment: 'bg-green-50 text-green-700 border-green-200', // Yeşil - Faiz Ödemesi
    investment_creation: 'bg-gold-50 text-gold-700 border-gold-200', // Mor - Yatırım Oluşturma
    investment_maturity: 'bg-cyan-50 text-cyan-700 border-cyan-200', // İndigo - Yatırım Vadesi
    penalty: 'bg-red-50 text-red-700 border-red-200',           // Kırmızı - Ceza
    fee: 'bg-yellow-50 text-yellow-700 border-yellow-200',      // Sarı - Ücret
    refund: 'bg-teal-50 text-teal-700 border-teal-200',         // Teal - İade
    investment: 'bg-cyan-50 text-cyan-700 border-cyan-200',     // Cyan - Yatırım
  } as Record<string, string>)[t] || 'bg-slate-50 text-slate-700 border-slate-200'

  const getInvestmentStatusLabel = (s: string) => ({
    active: t('Aktif','Aktiv'),
    inactive: t('Aktif Değil','Inaktiv'),
    cancelled: t('İptal','Storniert'),
  } as Record<string, string>)[s] || s

  const getInvestmentStatusColor = (s: string) => ({
    active: 'bg-green-50 text-green-700 border-green-200',     // Yeşil - Aktif
    inactive: 'bg-yellow-50 text-yellow-700 border-yellow-200', // Sarı - Aktif Değil
    cancelled: 'bg-red-50 text-red-700 border-red-200',       // Kırmızı - İptal
  } as Record<string, string>)[s] || 'bg-slate-50 text-slate-700 border-slate-200'

  const getTxStatusLabel = (s: string) => ({
    completed: t('Tamamlandı','Abgeschlossen'),
    pending: t('Beklemede','Ausstehend'),
    cancelled: t('İptal','Storniert'),
  } as Record<string, string>)[s] || s

  const getTxStatusColor = (s: string) => ({
    completed: 'bg-green-50 text-green-700 border-green-200',  // Yeşil
    pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',  // Sarı
    cancelled: 'bg-red-50 text-red-700 border-red-200',        // Kırmızı
  } as Record<string, string>)[s] || 'bg-slate-50 text-slate-700 border-slate-200'

  const formatCurrency = (n?: number) => {
    if (n === undefined || n === null) return ''
    return Number(n).toLocaleString('tr-TR')
  }
  const parseCurrency = (s: string): number | undefined => {
    const cleaned = s.replace(/\./g, '').replace(/[^0-9]/g, '')
    return cleaned ? Number(cleaned) : undefined
  }

  const calcExpected = (amount?: number, rate?: number, months?: number): string => {
    if (!amount || !rate || !months) return '-'
    const val = amount * (rate / 100) * (months / 12)
    return Number(val.toFixed(2)).toLocaleString('tr-TR', { maximumFractionDigits: 2 })
  }

  const [note, setNote] = useState('')
  const [notes, setNotes] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(()=>{
    // update quick calc
    if (!invForm.amount || !invForm.duration || !invForm.rate) { setCalc({}); return }
    const principal = Number(invForm.amount)
    const months = Number(invForm.duration)
    const rate = Number(invForm.rate)
    const expected = Number((principal * (rate/100) * (months/12)).toFixed(2))
    const maturity = new Date(Date.now() + months*30*24*60*60*1000).toISOString().slice(0,10)
    setCalc({ maturity, expected })
  },[invForm.amount, invForm.duration, invForm.rate])

  const fillSuggestedRate = async (bankId: number) => {
    try {
      const rates = await bankApi.rates.listByBank(bankId)
      if (rates && rates.length > 0) {
        // pick nearest term_days to entered duration or first
        const months = invForm.duration || 12
        const targetDays = months * 30
        let best = rates[0]
        let bestDiff = Math.abs((best.term_days||0) - targetDays)
        for (const r of rates) {
          const diff = Math.abs((r.term_days||0) - targetDays)
          if (diff < bestDiff) { best = r; bestDiff = diff }
        }
        setInvForm(v=>({ ...v, rate: Number(best.rate) }))
      }
    } catch { /* ignore */ }
  }

  const loadCustomers = async (query: string) => {
    try {
      if (role === 'staff') {
        const list = await callCenterApi.assignments.myCustomers()
        const mapped = list
          .map(c => ({ id: c.id, name: c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || `Müşteri #${c.id}` }))
          .filter(x => x.name.toLowerCase().includes(query.toLowerCase()))
        return mapped
      }
      if (!effectiveTeamId && role !== 'admin') return []
      if (role === 'admin' && requireTeamForAdmin && !effectiveTeamId) return []
      try {
        const res = await apiClient.get('/api/v1/users', { params: { role: 'customer', page_size: 100, team_id: effectiveTeamId, search: query || undefined } })
        return (res.data?.users || []).map((u: any) => ({ id: u.id, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email, email: u.email }))
      } catch (err: any) {
        if (err?.response?.status === 403) {
          const list = await callCenterApi.assignments.myCustomers()
          return list.map(c => ({ id: c.id, name: c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || `Müşteri #${c.id}` }))
        }
        return []
      }
    } catch {
      return []
    }
  }

  const loadCustomerProfile = async (id: number) => {
    // Fetch basic profile
    try {
      const res = await apiClient.get(`/api/v1/users/${id}`)
      setCustomerProfile(res.data)
    } catch {
      setCustomerProfile(null)
      return
    }
    // Fetch overview (best effort)
    try {
      const ov = await apiClient.get(`/api/v1/callcenter/customers/${id}/overview`)
      setCustomerProfile((prev:any)=> ({ ...(prev||{}), __overview: ov.data?.data }))
    } catch { /* ignore */ }
  }

  const fetchTransactions = async () => {
    if (!selectedCustomer) return
    try {
      const tx = await apiClient.get('/api/v1/transactions', { params: { user_id: selectedCustomer.id, page_size: 50 } })
      setTxList(tx.data?.transactions || tx.data?.items || [])
    } catch (error: any) {
      setTxList([]);
    }
  }

  const fetchDocs = async () => {
    if (!selectedCustomer) return
    try {
      const res = await apiClient.get('/api/v1/files/list', { params: { page_size: 100, user_files_only: false, search: `cust_${selectedCustomer.id}` } })
      setDocs(res.data?.files || [])
    } catch { setDocs([]) }
  }

  const fetchInvestments = async () => {
    if (!selectedCustomer) return
    try {
      const res = await apiClient.get(`/api/v1/callcenter/customers/${selectedCustomer.id}/investments`)
      setInvestments(res.data?.data || [])
    } catch { setInvestments([]) }
  }

  const createInvestment = async () => {
    if (!selectedCustomer || !invForm.amount || !invForm.duration || !invForm.rate || !(invForm.bank_id || selectedBank?.id)) {
      toast.error('Tüm alanları doldurun')
      return
    }
    try {
      await apiClient.post(`/api/v1/callcenter/customers/${selectedCustomer.id}/investments`, null, {
        params: {
          amount: invForm.amount,
          duration_months: invForm.duration,
          interest_rate: invForm.rate,
          bank_id: invForm.bank_id || selectedBank?.id,
          notes: invForm.notes || '',
          status: invForm.status || 'active'
        }
      })
      toast.success('Yatırım oluşturuldu')
      setInvForm({})
      setSelectedBank(null)
      fetchInvestments()
    } catch (e:any) { toast.error(e?.response?.data?.detail || 'Yatırım oluşturulamadı') }
  }

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedCustomer) return
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('category', 'documents')
      form.append('description', `Customer document for ${selectedCustomer.name}`)
      form.append('tags', JSON.stringify([`cust_${selectedCustomer.id}`]))
      const res = await apiClient.post('/api/v1/files/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (res.data?.file) toast.success('Doküman yüklendi')
      await fetchDocs()
    } catch (err:any) { toast.error(err?.response?.data?.detail || 'Yükleme başarısız') } finally { setUploading(false); (e.target as any).value='' }
  }

  const fetchNotes = async () => {
    if (!selectedCustomer) return
    setLoading(true)
    try {
      const list = await callCenterApi.notes.list(selectedCustomer.id)
      setNotes(list)
    } catch (e) { toast.error('Notlar getirilemedi') } finally { setLoading(false) }
  }

  const createNote = async () => {
    if (!selectedCustomer || !note.trim()) return
    try {
      await callCenterApi.notes.create(selectedCustomer.id, note.trim())
      toast.success('Not eklendi')
      setNote('')
      fetchNotes()
    } catch (e: any) {
      const msg = e?.response?.data?.detail || (role === 'staff' ? 'Aktif atama yok: Bu müşteriye atanmış olmanız gerekir' : 'Not eklenemedi')
      toast.error(msg)
    }
  }

  const onSelectCustomer = (item: SelectCustomerItem) => {
    setSelectedCustomer(item)
    setNotes([])
    setCustomerProfile(null)
    setActiveTab('info')
    loadCustomerProfile(item.id)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="pb-5 border-b border-slate-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Callcenter</p>
        <h1 className="text-2xl font-serif font-medium text-slate-900">{t('Görüşmeler','Gespräche')}</h1>
        <p className="text-slate-600 mt-1">{t('Müşteri görüşmeleri, bilgileri ve dokümanları','Kundengespräche, Informationen und Dokumente')}</p>
      </div>

      {/* Customer header */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={()=>setModalOpen(true)} disabled={role==='admin' && requireTeamForAdmin && !effectiveTeamId}>
            {selectedCustomer ? selectedCustomer.name : t('Müşteri Seç','Kunde auswählen')}
          </Button>
          {selectedCustomer && (
            <div className="text-sm text-slate-600">{t('Seçili müşteri:','Ausgewählter Kunde:')} <span className="font-medium text-slate-900">{selectedCustomer.name}</span></div>
          )}
        </div>
        {selectedCustomer && (
          <div className="mt-4 border-b border-slate-200 flex gap-4 text-sm">
            <button className={`pb-2 ${activeTab==='info'?'border-b-2 border-primary-600 text-primary-600 font-medium':'text-slate-500'}`} onClick={()=>setActiveTab('info')}>{t('Bilgiler','Informationen')}</button>
            <button className={`pb-2 ${activeTab==='notes'?'border-b-2 border-primary-600 text-primary-600 font-medium':'text-slate-500'}`} onClick={()=>{setActiveTab('notes'); fetchNotes()}}>{t('Görüşmeler','Gespräche')}</button>
            <button className={`pb-2 ${activeTab==='docs'?'border-b-2 border-primary-600 text-primary-600 font-medium':'text-slate-500'}`} onClick={()=>{setActiveTab('docs'); fetchDocs()}}>{t('Dokümanlar','Dokumente')}</button>
            <button className={`pb-2 ${activeTab==='investments'?'border-b-2 border-primary-600 text-primary-600 font-medium':'text-slate-500'}`} onClick={()=>{setActiveTab('investments'); fetchInvestments()}}>{t('Yatırımlar','Anlagen')}</button>
            <button className={`pb-2 ${activeTab==='transactions'?'border-b-2 border-primary-600 text-primary-600 font-medium':'text-slate-500'}`} onClick={()=>{ setActiveTab('transactions'); fetchTransactions() }}>{t('İşlemler','Transaktionen')}</button>
          </div>
        )}
      </div>

      {/* Tab content */}
      {selectedCustomer && activeTab==='info' && (
        <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">{t('Müşteri Bilgileri','Kundeninformationen')}</h2>
          {!customerProfile ? (
            <p className="text-slate-500">{t('Bilgiler yükleniyor veya bulunamadı','Informationen werden geladen oder nicht gefunden')}</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Ad Soyad','Vorname Nachname')}</p>
                  <p className="font-medium text-slate-900">{customerProfile.first_name} {customerProfile.last_name}</p>
                </div>
                {isManager && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Email','E-Mail')}</p>
                    <p className="font-medium text-slate-900">{customerProfile.email}</p>
                  </div>
                )}
                {isManager && customerProfile.phone && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Telefon','Telefon')}</p>
                    <p className="font-medium text-slate-900">{customerProfile.phone}</p>
                  </div>
                )}
              </div>
              {/* Overview cards */}
              {customerProfile.__overview && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6 border-t border-slate-100">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Toplam Yatırım','Gesamtanlage')}</p>
                    <p className="text-xl font-serif font-medium text-slate-900 tabular-nums">{formatCurrency(Number(customerProfile.__overview.portfolio_stats.total_investment))}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Güncel Değer','Aktueller Wert')}</p>
                    <p className="text-xl font-serif font-medium text-slate-900 tabular-nums">{formatCurrency(Number(customerProfile.__overview.portfolio_stats.total_value))}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Kâr','Gewinn')}</p>
                    <p className="text-xl font-serif font-medium text-primary-600 tabular-nums">{formatCurrency(Number(customerProfile.__overview.portfolio_stats.total_profit))}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Aktif / Tamamlanan','Aktiv / Abgeschlossen')}</p>
                    <p className="text-xl font-serif font-medium text-slate-900 tabular-nums">{customerProfile.__overview.portfolio_stats.active_investments} / {customerProfile.__overview.portfolio_stats.completed_investments}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {selectedCustomer && (
        <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Seçili müşteri:','Ausgewählter Kunde:')}</p>
              <p className="font-medium text-slate-900">{customerProfile?.first_name} {customerProfile?.last_name}</p>
              <p className="text-xs text-slate-500">{customerProfile?.email}</p>
            </div>
          </div>
          {/* Callcenter only extra application fields */}
          {(role === 'staff' || role === 'callcenter_admin' || role === 'admin') && customerProfile && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div className="p-3 border border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Başvurulan Banka','Bewerbte Bank')}</p>
                <p className="font-medium text-slate-900">{customerProfile.applied_bank || '-'}</p>
              </div>
              <div className="p-3 border border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Başvurulan Tutar','Bewerbte Summe')}</p>
                <p className="font-medium text-slate-900">{customerProfile.applied_amount ? `€${Number(customerProfile.applied_amount).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}` : '-'}</p>
              </div>
              <div className="p-3 border border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Faiz Teklifi','Zinssatzangebot')}</p>
                <p className="font-medium text-slate-900">{customerProfile.offered_interest_rate ? `%${Number(customerProfile.offered_interest_rate).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}` : '-'}</p>
              </div>
              <div className="p-3 border border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('Başvurulan Website','Bewerbte Website')}</p>
                <p className="font-medium text-slate-900 break-all">{customerProfile.application_website || '-'}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedCustomer && activeTab==='notes' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6 space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Görüşme Notu Ekle','Gesprächsnotiz hinzufügen')}</h2>
            <div className="flex gap-2 items-center">
              <input value={note} onChange={(e)=>setNote(e.target.value)} placeholder={t('Görüşme notu','Gesprächsnotiz')} className={inputCls + " flex-1"}/>
              <Button onClick={createNote}>{t('Ekle','Hinzufügen')}</Button>
              <Button variant="outline" onClick={fetchNotes} disabled={loading}>{t('Notları Getir','Noten abrufen')}</Button>
            </div>
          </div>
          <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">{t('Görüşme Geçmişi','Gesprächsverlauf')}</h2>
            {notes.length === 0 ? (
              <p className="text-slate-500">{t('Kayıt yok','Keine Aufzeichnungen')}</p>
            ) : (
              <div className="space-y-2">
                {notes.map((n:any)=> (
                  <div key={n.id} className="p-3 border border-slate-200 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{n.note_text}</p>
                      <p className="text-xs text-slate-500">{n.created_at} • {n.agent_full_name || `Personel #${n.agent_id}`} (ID: {n.agent_id})</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedCustomer && activeTab==='docs' && (
        <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Dokümanlar','Dokumente')}</h2>
            <label className={ghostBtnCls + " cursor-pointer"}>
              {t('Dosya Yükle','Datei hochladen')}
              <input type="file" className="hidden" onChange={onUpload} disabled={uploading} />
            </label>
          </div>
          {docs.length === 0 ? (
            <p className="text-slate-500">{t('Kayıt yok','Keine Aufzeichnungen')}</p>
          ) : (
            <div className="space-y-2">
              {docs.map((d:any)=> (
                <div key={d.id} className="p-3 border border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{d.original_name}</p>
                    <p className="text-xs text-slate-500">{d.mime_type} • {Math.round((d.size||0)/1024)} KB</p>
                    <p className="text-xs text-slate-500">{t('Yükleyen','Hochgeladen von')}: {d.uploader_full_name || `User #${d.uploaded_by}`} • {d.uploaded_at}</p>
                  </div>
                  <a className={smallGhostBtnCls} href={d.url?.startsWith('/api')?d.url:`/api/v1${d.url}`} target="_blank">{t('İndir','Herunterladen')}</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedCustomer && activeTab==='investments' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">{t('Yeni Yatırım Oluştur','Neue Anlage erstellen')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <input type="text" placeholder={t('Tutar','Summe')} value={invForm.amount !== undefined ? formatCurrency(invForm.amount) : ''} onChange={e=>setInvForm({...invForm, amount: parseCurrency(e.target.value)})} className={inputCls}/>
              <input type="number" placeholder={t('Vade (ay)','Laufzeit (Monate)')} value={invForm.duration || ''} onChange={async (e)=>{ const v=Number(e.target.value); setInvForm({...invForm, duration: v}); if (selectedBank) { await fillSuggestedRate(selectedBank.id) } }} className={inputCls}/>
              <input type="number" step="0.01" placeholder={t('Faiz (%)','Zinssatz (%)')} value={invForm.rate || ''} onChange={e=>setInvForm({...invForm, rate: Number(e.target.value)})} className={inputCls}/>
              <button type="button" onClick={()=>setBankModal(true)} className={ghostBtnCls + " text-left"}>
                {selectedBank ? `${t('Banka','Bank')}: ${selectedBank.name}` : t('Banka Seç','Bank auswählen')}
              </button>
              <select value={invForm.status || 'active'} onChange={(e)=>setInvForm({...invForm, status: e.target.value})} className={inputCls}>
                <option value="active">{t('Aktif','Aktiv')}</option>
                <option value="inactive">{t('Aktif Değil','Inaktiv')}</option>
                <option value="cancelled">{t('İptal','Storniert')}</option>
              </select>
              <Button onClick={createInvestment}>{t('Kaydet','Speichern')}</Button>
            </div>
            <textarea placeholder={t('Notlar (opsiyonel)','Noten (optional)')} value={invForm.notes || ''} onChange={e=>setInvForm({...invForm, notes: e.target.value})} className={inputCls + " mt-3 w-full"} rows={3}/>
            {calc.maturity || calc.expected ? (
              <div className="mt-2 text-sm text-slate-600">{t('Vade bitiş','Fälligkeitsdatum')}: {calc.maturity || '-'} • {t('Tahmini getiri','Voraussichtliche Rendite')}: {calc.expected?.toLocaleString('tr-TR', {maximumFractionDigits:2}) || '-'}</div>
            ) : null}
          </div>
          <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">{t('Geçmiş Yatırımlar','Vergangene Anlagen')}</h2>
            {investments.length === 0 ? (
              <p className="text-slate-500">{t('Kayıt yok','Keine Aufzeichnungen')}</p>
            ) : (
              <div className="space-y-2">
                {investments.map((inv:any)=> (
                  <div key={inv.id} className="p-3 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-900">{inv.bank_name || `${t('Banka','Bank')} #${inv.bank_id}`} • {formatCurrency(inv.amount)} • {inv.duration_months} {t('ay','Monate')} • %{inv.interest_rate}</p>
                          <span className={`px-2 py-0.5 border text-xs font-medium ${getInvestmentStatusColor(inv.status)}`}>
                            {getInvestmentStatusLabel(inv.status)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">{inv.start_date} • {t('Tahmini getiri','Voraussichtliche Rendite')}: {calcExpected(Number(inv.amount), Number(inv.interest_rate), Number(inv.duration_months))} • {t('Oluşturan','Erstellt von')}: {inv.created_by_name || `${t('Kullanıcı','Benutzer')} #${inv.created_by_user_id}`}</p>
                    </div>
                      {(isManager || role === 'staff') && (
                        <div className="flex gap-2 items-center">
                          <select
                            value={inv.status}
                            onChange={async (e) => {
                              try {
                                await apiClient.patch(`/api/v1/investments/${inv.id}/status`, {
                                  status: e.target.value
                                });
                                toast.success('Yatırım durumu güncellendi');
                                await fetchInvestments();
                              } catch(err: any) {
                                toast.error(err?.response?.data?.detail || 'Durum güncellenemedi');
                              }
                            }}
                            className={smallSelectCls}
                          >
                            <option value="active">{t('Aktif','Aktiv')}</option>
                            <option value="inactive">{t('Aktif Değil','Inaktiv')}</option>
                            <option value="cancelled">{t('İptal','Storniert')}</option>
                          </select>
                          <button className={smallGhostBtnCls} disabled={inv.status==='cancelled'} onClick={()=>{ setEditTarget(inv); setEditOpen(true) }}>{t('Düzenle','Bearbeiten')}</button>
                        <button className={smallGhostBtnCls + " text-danger-600"} disabled={inv.status==='cancelled'} onClick={async()=>{
                          const reason = window.prompt('İptal sebebi (opsiyonel):') || undefined
                          try {
                            await apiClient.post(`/api/v1/callcenter/customers/${selectedCustomer!.id}/investments/${inv.id}/cancel`, { reason })
                            toast.success('Yatırım iptal edildi')
                            fetchInvestments()
                          } catch(e:any){ toast.error(e?.response?.data?.detail || 'İptal edilemedi') }
                          }}>{t('İptal','Stornieren')}</button>
                      </div>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Edit Modal */}
          {editOpen && editTarget && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-primary-950/50" onClick={()=>setEditOpen(false)} />
              <div className="relative bg-white w-full max-w-lg border-t-2 border-t-primary-600 shadow-xl p-5 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Yatırım Düzenle','Anlage bearbeiten')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input type="number" placeholder={t('Tutar','Summe')} defaultValue={editTarget.amount} onChange={(e)=> setEditTarget({...editTarget, amount: Number(e.target.value)})} className={inputCls}/>
                  <input type="number" placeholder={t('Vade (ay)','Laufzeit (Monate)')} defaultValue={editTarget.duration_months} onChange={(e)=> setEditTarget({...editTarget, duration_months: Number(e.target.value)})} className={inputCls}/>
                  <input type="number" step="0.01" placeholder={t('Faiz (%)','Zinssatz (%)')} defaultValue={editTarget.interest_rate} onChange={(e)=> setEditTarget({...editTarget, interest_rate: Number(e.target.value)})} className={inputCls}/>
                </div>
                <textarea placeholder={t('Notlar','Noten')} defaultValue={editTarget.notes || ''} onChange={(e)=> setEditTarget({...editTarget, notes: e.target.value})} className={inputCls + " w-full"} rows={3} />
                <div className="flex justify-end gap-2">
                  <button className={ghostBtnCls} onClick={()=>setEditOpen(false)}>{t('Vazgeç','Abbrechen')}</button>
                  <button className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium" onClick={async()=>{
                    try {
                      await apiClient.put(`/api/v1/callcenter/customers/${selectedCustomer!.id}/investments/${editTarget.id}`, {
                        amount: editTarget.amount,
                        duration_months: editTarget.duration_months,
                        interest_rate: editTarget.interest_rate,
                        notes: editTarget.notes
                      })
                      toast.success('Yatırım güncellendi')
                      setEditOpen(false); setEditTarget(null); fetchInvestments()
                    } catch(e:any){ toast.error(e?.response?.data?.detail || 'Güncellenemedi') }
                  }}>{t('Kaydet','Speichern')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedCustomer && activeTab==='transactions' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">{t('Yeni İşlem Oluştur','Neue Transaktion erstellen')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <select value={txForm.type || ''} onChange={(e)=>setTxForm({...txForm, type: e.target.value})} className={inputCls}>
                <option value="">{t('Tür Seçin','Transaktionsart auswählen')}</option>
                <option value="deposit">{t('Para Yatırma','Einzahlung')}</option>
                <option value="bonus">{t('Yeni Müşteri Bonusu','Neukundenbonus')}</option>
                <option value="withdrawal">{t('Para Çekme','Auszahlung')}</option>
                <option value="interest_payment">{t('Faiz Ödemesi','Zinszahlung')}</option>
                <option value="refund">{t('İade','Rückerstattung')}</option>
              </select>
              <input type="text" placeholder={t('Tutar','Summe')} value={txForm.amount !== undefined ? formatCurrency(txForm.amount) : ''} onChange={(e)=>setTxForm({...txForm, amount: parseCurrency(e.target.value)})} className={inputCls}/>
              <select value={txForm.status || 'pending'} onChange={(e)=>setTxForm({...txForm, status: e.target.value})} className={inputCls}>
                <option value="pending">{t('Beklemede','Ausstehend')}</option>
                <option value="completed">{t('Tamamlandı','Abgeschlossen')}</option>
                <option value="cancelled">{t('İptal','Storniert')}</option>
              </select>
              <input type="text" placeholder={t('Referans Kodu','Referenznummer')} value={txForm.reference_number || ''} onChange={(e)=>setTxForm({...txForm, reference_number: e.target.value})} className={inputCls}/>
              <Button variant="outline" onClick={()=>setTxForm({...txForm, reference_number: `REF-${Date.now().toString().slice(-8)}`})}>{t('Referans Oluştur','Referenz erstellen')}</Button>
              <Button onClick={async()=>{
                if (!txForm.type || !txForm.amount) { toast.error('Tür ve Tutar zorunlu'); return }
                try {
                  await apiClient.post('/api/v1/transactions', {
                    user_id: selectedCustomer!.id,
                    type: txForm.type,
                    amount: txForm.amount,
                    description: txForm.description || '',
                    reference_number: txForm.reference_number || undefined,
                    status: txForm.status || 'pending'
                  })
                  toast.success('İşlem oluşturuldu')
                  await fetchTransactions()
                  setTxForm({})
                } catch(e:any){ toast.error(e?.response?.data?.detail || 'İşlem oluşturulamadı') }
              }}>{t('Kaydet','Speichern')}</Button>
            </div>
            <textarea placeholder={t('Açıklama (opsiyonel)','Beschreibung (optional)')} value={txForm.description || ''} onChange={(e)=>setTxForm({...txForm, description: e.target.value})} className={inputCls + " mt-3 w-full"} rows={3}/>
            <p className="text-xs text-slate-500 mt-2">{t('Not','Hinweis')}: {t('Durum alanı bilgilendirme amaçlıdır; zorunlu değildir.','Der Statusfeld ist nur informativen Zweck erfüllt; nicht zwingend erforderlich.')}</p>
          </div>
          <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">{t('İşlem Geçmişi','Transaktionsverlauf')}</h2>
            {txList.length === 0 ? (<p className="text-slate-500">{t('Kayıt yok','Keine Aufzeichnungen')}</p>) : (
              <div className="space-y-2">
                {txList.map((t:any)=> (
                  <div key={t.id} className={`p-3 border border-slate-200 grid grid-cols-1 ${(isManager || role === 'staff') ? 'md:grid-cols-7' : 'md:grid-cols-6'} gap-2 text-sm`}>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('Tür','Transaktionsart')}</div><div><span className={`inline-flex px-2 py-0.5 border text-xs font-medium ${getTxTypeColor(t.type)}`}>{getTxTypeLabel(t.type)}</span></div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('Açıklama','Beschreibung')}</div><div className="font-medium text-slate-900">{t.description || '-'}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('Tutar','Summe')}</div><div className="font-medium text-slate-900 tabular-nums">€{formatCurrency(Number(t.amount))}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('Durum','Status')}</div><div><span className={`inline-flex px-2 py-0.5 border text-xs font-medium ${getTxStatusColor(t.status)}`}>{getTxStatusLabel(t.status)}</span></div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('Referans','Referenz')}</div><div className="font-medium text-slate-900">{t.reference_number || '-'}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('Tarih','Datum')}</div><div className="font-medium text-slate-900">{t.created_at}</div></div>
                    {(isManager || role === 'staff') && (
                      <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{t('İşlem','Transaktion')}</div><div>
                        <select
                          value={t.status}
                          onChange={async (e) => {
                            try {
                              await apiClient.patch(`/api/v1/transactions/${t.id}/status`, {
                                status: e.target.value
                              });
                              toast.success('İşlem durumu güncellendi');
                              await fetchTransactions();
                            } catch(err: any) {
                              toast.error(err?.response?.data?.detail || 'Durum güncellenemedi');
                            }
                          }}
                          className={smallSelectCls}
                        >
                          <option value="pending">{t('Beklemede','Ausstehend')}</option>
                          <option value="completed">{t('Tamamlandı','Abgeschlossen')}</option>
                          <option value="cancelled">{t('İptal','Storniert')}</option>
                        </select>
                      </div></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <CustomerSelectModal
        isOpen={modalOpen}
        onClose={()=>setModalOpen(false)}
        onSelect={(item)=>{ onSelectCustomer(item); setModalOpen(false); fetchDocs(); }}
        loader={loadCustomers}
        title={t('Müşteri Seç','Kunde auswählen')}
      />
      <BankSelectModal isOpen={bankModal} onClose={()=>setBankModal(false)} onSelect={(b)=>{ setSelectedBank(b); setInvForm({...invForm, bank_id: b.id}); setBankModal(false); fillSuggestedRate(b.id) }} />
    </div>
  )
}

export default CallCenterConversations
